import { CircularProgress, Divider } from '@mui/material'
import CustomPaper from '../../containers/Paper'
import { useContext, useEffect, useState } from 'react'
import { LastChatInfo } from '../../models/chat'
import ChatService from '../../services/ChatService'
import UserService from '../../services/UserService'
import { UserDTO } from '../../models/users'
import {
    dateFormatter,
    isAdmin,
    isDateToday,
    scrollToChildInsideParent,
    sortDates,
} from '../../helpers/utils'
import { Navigate, useLocation } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import DoneIcon from '@mui/icons-material/Done'
import dayjs from 'dayjs'
import Chat from './Chat'
import { StompSessionProvider } from 'react-stomp-hooks'
import ChatIcon from '@mui/icons-material/Chat'
import CustomModal from '../../containers/Modal'
import SearchGraduatesModal from '../../components/Modal/SearchGraduatesModal'
import NoResults from '../../containers/NoResults'
import { PageContext } from '../../context/PageContext'

const MessagesPage = () => {
    const [currentUser, setCurrentUser] = useState<UserDTO>()
    const [chats, setChats] = useState<LastChatInfo[]>()
    const [selectedChat, setSelectedChat] = useState<LastChatInfo>()
    const [mutate, setMutate] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const { state } = useLocation()
    const { smallScreen } = useContext(PageContext)

    useEffect(() => {
        new UserService().getCurrentUser().then((response) => {
            if (response.status !== 200) return
            setCurrentUser(response.data)
        })
    }, [])

    useEffect(() => {
        if (!currentUser) return
        new ChatService().getAllChats(currentUser.id).then((response) => {
            if (response.status !== 200) return
            const chatsResponse: LastChatInfo[] = response.data
            chatsResponse.sort((x, y) =>
                sortDates(
                    x.lastMessage?.timestamp ? new Date(x.lastMessage.timestamp) : new Date(),
                    y.lastMessage?.timestamp ? new Date(y.lastMessage.timestamp) : new Date(),
                ),
            )
            if (
                state?.messageTo &&
                !chatsResponse.some((x) => x.recipient.id === state.messageTo.id)
            ) {
                const newChat: LastChatInfo = {
                    recipient: state.messageTo,
                    lastMessage: undefined,
                    sentByMe: false,
                }
                chatsResponse.unshift(newChat)
            }
            if (!selectedChat && chatsResponse.length > 0) {
                if (state?.messageTo) {
                    setSelectedChat(
                        chatsResponse.find((x) => x.recipient.id === state.messageTo.id),
                    )
                } else setSelectedChat(chatsResponse.at(0))
            }
            if (
                selectedChat &&
                !selectedChat.lastMessage &&
                !chatsResponse.some((x) => x.recipient.id === selectedChat.recipient.id)
            )
                setChats([selectedChat, ...chatsResponse])
            else setChats(chatsResponse)
        })
    }, [currentUser, mutate, state, selectedChat])

    const handleNewMessage = (user: UserDTO) => {
        if (!chats) return
        const existingChat = chats.find((chat) => chat.recipient.id === user.id)
        if (existingChat) {
            setSelectedChat(existingChat)
            scrollToChildInsideParent('chatList', `chat${user.id}`)
        } else {
            const newChat: LastChatInfo = {
                recipient: user,
                lastMessage: undefined,
                sentByMe: false,
            }
            setSelectedChat(newChat)
            setChats((prev) => [newChat, ...prev!])
        }
    }

    const LastChatBox = ({ chat }: { chat: LastChatInfo }) => {
        return (
            <div className="flex flex-col">
                <div
                    className={`relative flex justify-center md:justify-normal gap-2 md:gap-4 py-2 md:py-4 cursor-pointer px-2 md:px-5 items-center ${selectedChat && selectedChat.recipient.id === chat.recipient.id ? 'bg-optional' : ''}`}
                    key={chat.recipient.id}
                    onClick={() => setSelectedChat(chat)}
                >
                    <img
                        src={!!chat.recipient.picture ? chat.recipient.picture : '/adminPhoto.png'}
                        alt="chatter"
                        className="h-10 md:h-12 w-10 md:w-12 rounded-[100%] outline outline-primary outline-1"
                    />
                    {!smallScreen && (
                        <>
                            <div className="flex flex-col justify-around text-secondary overflow-hidden">
                                <span
                                    title={chat.recipient.firstName + ' ' + chat.recipient.lastName}
                                    className="text-lg font-semibold line-clamp-1 break-all mr-12"
                                >
                                    {chat.recipient.firstName + ' ' + chat.recipient.lastName}
                                </span>

                                {chat.lastMessage && (
                                    <div className="flex gap-1 items-center">
                                        {chat.sentByMe && (
                                            <DoneIcon className="text-sm md:text-lg text-primary" />
                                        )}
                                        <span className="break-all line-clamp-1 overflow-ellipsis">
                                            {chat.lastMessage.content}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {chat.lastMessage && (
                                <span className="absolute top-[15%] right-[6%] text-sm text-gray">
                                    {isDateToday(new Date(chat.lastMessage.timestamp))
                                        ? dayjs(chat.lastMessage.timestamp).format('HH:mm')
                                        : dateFormatter(new Date(chat.lastMessage.timestamp))}
                                </span>
                            )}
                        </>
                    )}
                </div>
                <Divider className="p-0" />
            </div>
        )
    }

    return (
        <StompSessionProvider url={`${process.env.REACT_APP_API_URL}/ws`}>
            {!isAdmin() ? (
                <>
                    {currentUser && chats ? (
                        <CustomPaper className="relative flex h-[calc(100vh-132px)] w-full p-0 overflow-hidden">
                            <div className="h-full w-[25%] shadow-[1px_0px_16px_0px_rgba(31,_34,_50,_0.25)] flex flex-col">
                                <>
                                    <div
                                        className="flex flex-col md:flex-row text-center md:text-left gap-2 md:gap-4 p-[15px] md:p-[26px] text-secondary font-semibold text-sm md:text-lg items-center cursor-pointer"
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        <ChatIcon />
                                        <span>Crear nuevo chat</span>
                                    </div>
                                    <Divider />
                                </>
                                <div
                                    className="flex flex-col overflow-y-auto"
                                    id="chatList"
                                >
                                    {chats.map((x) => (
                                        <div
                                            id={`chat${x.recipient.id}`}
                                            key={x.recipient.id}
                                        >
                                            <LastChatBox chat={x} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {currentUser ? (
                                selectedChat ? (
                                    <Chat
                                        thisUserId={currentUser.id}
                                        recipient={selectedChat.recipient}
                                        updatePage={() => setMutate((prev) => !prev)}
                                    />
                                ) : (
                                    <div className="w-fit h-fit px-3 md:px-10 py-5">
                                        <NoResults
                                            title="No tenés conversaciones aún"
                                            subtitle="Podés pulsar el botón para comenzar a chatear con cualquier graduado"
                                        />
                                    </div>
                                )
                            ) : (
                                <div className="flex w-full justify-center">
                                    <CircularProgress
                                        className="m-2 md:m-5 text-primary"
                                        size={64}
                                    />
                                </div>
                            )}
                        </CustomPaper>
                    ) : (
                        <div className="flex w-full justify-center">
                            <CircularProgress
                                className="m-2 md:m-5 text-primary"
                                size={64}
                            />
                        </div>
                    )}
                    <CustomModal
                        isOpen={isModalOpen}
                        handleClose={() => setIsModalOpen(false)}
                    >
                        <SearchGraduatesModal
                            text="Buscá y seleccioná un graduado para chatear"
                            handleClose={() => setIsModalOpen(false)}
                            handleSelectGraduate={handleNewMessage}
                        />
                    </CustomModal>
                </>
            ) : (
                <Navigate to={AppRoutes.login} />
            )}
        </StompSessionProvider>
    )
}

export default MessagesPage
